import { signal } from "@preact/signals";
import { XP_PER_TRAINING_CORRECT } from "@/config";
import { answerText, computeHint, givenText } from "@/battle/BattleController";
import {
  accuracyOf,
  checkAnswer,
  createRng,
  generateQuestion,
  pickTier,
  pickTopic,
  randomSeed,
  updateStat,
  type LocalizedText,
  type Question,
  type Tier,
} from "@/math";
import { applyXp, hero, heroTopics, recordAttempt, settings } from "@/state/store";

export type TrainingTier = Tier | "auto";
export type TrainingPhase = "setup" | "question" | "feedback";

export interface TrainingFeedback {
  correct: boolean;
  explanation: { pl: string[]; en: string[] };
  correctAnswerText: LocalizedText;
}

export interface TrainingState {
  topicId: string | "mixed";
  tier: TrainingTier;
  phase: TrainingPhase;
  question: Question | null;
  questionStartedAt: number;
  feedback: TrainingFeedback | null;
  answered: number;
  correct: number;
  streak: number;
  bestStreak: number;
  /** Hint state for the current question (hints are free in training). */
  eliminated: number[];
  hintText: LocalizedText | null;
  hintsUsed: number;
}

export const training = signal<TrainingState | null>(null);

export function openTraining(): void {
  training.value = {
    topicId: "mixed",
    tier: "auto",
    phase: "setup",
    question: null,
    questionStartedAt: 0,
    feedback: null,
    answered: 0,
    correct: 0,
    streak: 0,
    bestStreak: 0,
    eliminated: [],
    hintText: null,
    hintsUsed: 0,
  };
}

export function closeTraining(): void {
  training.value = null;
}

export function startTraining(topicId: string | "mixed", tier: TrainingTier): void {
  const s = training.value;
  if (!s) return;
  training.value = withNextQuestion({
    ...s,
    topicId,
    tier,
    answered: 0,
    correct: 0,
    streak: 0,
    bestStreak: 0,
    hintsUsed: 0,
  });
}

export function backToSetup(): void {
  const s = training.value;
  if (!s) return;
  training.value = { ...s, phase: "setup", question: null, feedback: null, eliminated: [], hintText: null };
}

function withNextQuestion(s: TrainingState): TrainingState {
  const h = hero.value;
  const rng = createRng(randomSeed());
  const topics = heroTopics(h);
  const topicId =
    s.topicId === "mixed" ? pickTopic({ topics, stats: h.stats, recent: h.recentTopics, rng }) : s.topicId;
  const tier: Tier = s.tier === "auto" ? pickTier("common", accuracyOf(h.stats[topicId]), rng) : s.tier;
  const question = generateQuestion(topicId, tier, randomSeed(), settings.value.preference);
  return {
    ...s,
    phase: "question",
    question,
    questionStartedAt: performance.now(),
    feedback: null,
    eliminated: [],
    hintText: null,
  };
}

/** Hints are free and unlimited in training (one per question). */
export function hintTraining(): void {
  const s = training.value;
  if (!s || s.phase !== "question" || !s.question || s.eliminated.length > 0 || s.hintText) return;
  const { eliminated, hintText } = computeHint(s.question);
  training.value = { ...s, eliminated, hintText, hintsUsed: s.hintsUsed + 1 };
}

export function answerTraining(input: string | number): void {
  const s = training.value;
  if (!s || s.phase !== "question" || !s.question) return;
  const q = s.question;
  const elapsed = Math.round(performance.now() - s.questionStartedAt);
  const correct = checkAnswer(q.answer, input);
  const h = hero.value;
  const stats = { ...h.stats, [q.topicId]: updateStat(h.stats[q.topicId], correct, elapsed) };
  const recentTopics = [...h.recentTopics, q.topicId].slice(-5);
  const { hero: h2 } = applyXp(
    { ...h, stats, recentTopics, answered: h.answered + 1, correct: h.correct + (correct ? 1 : 0) },
    correct ? XP_PER_TRAINING_CORRECT : 0,
  );
  hero.value = recordAttempt(h2, {
    at: new Date().toISOString(),
    topicId: q.topicId,
    tier: q.tier,
    prompt: q.prompt.pl,
    answer: answerText(q).pl,
    given: givenText(q, input),
    correct,
    timeMs: elapsed,
    context: "training",
  });
  const streak = correct ? s.streak + 1 : 0;
  training.value = {
    ...s,
    phase: "feedback",
    answered: s.answered + 1,
    correct: s.correct + (correct ? 1 : 0),
    streak,
    bestStreak: Math.max(s.bestStreak, streak),
    feedback: { correct, explanation: q.explanation, correctAnswerText: answerText(q) },
  };
}

export function nextTraining(): void {
  const s = training.value;
  if (!s || s.phase !== "feedback") return;
  training.value = withNextQuestion(s);
}
